/**
 * #2214 — compact at-a-glance pills showing what a task requires (Ord / Inv /
 * OOS / Photo / Doc). Rendered on TaskCard so on Today (or anywhere a card
 * shows) it's obvious what each task needs — a big help for testing and rep
 * clarity. Flags come from the project (requires_order/inventory/oos #2022,
 * requires_photos/documents #2077).
 */

export interface TaskRequirements {
  order?: boolean;
  inventory?: boolean;
  oos?: boolean;
  photos?: boolean;
  documents?: boolean;
}

interface PillDef {
  key: keyof TaskRequirements;
  label: string;
  title: string;
  cls: string;
}

// Order matches the onsite workflow sequence; colors are distinct + match the
// rest of the app's palette (order=cyan like a call, oos=amber like a set).
const PILL_DEFS: PillDef[] = [
  { key: 'order', label: 'Ord', title: 'Order', cls: 'bg-cyan-100 text-cyan-700' },
  { key: 'inventory', label: 'Inv', title: 'Inventory', cls: 'bg-indigo-100 text-indigo-700' },
  { key: 'oos', label: 'OOS', title: 'Out of stock', cls: 'bg-amber-100 text-amber-700' },
  { key: 'photos', label: 'Photo', title: 'Photo', cls: 'bg-pink-100 text-pink-700' },
  { key: 'documents', label: 'Doc', title: 'Document', cls: 'bg-slate-200 text-slate-700' },
];

export function hasAnyRequirement(requirements?: TaskRequirements): boolean {
  return !!requirements && PILL_DEFS.some((d) => requirements[d.key]);
}

/** Map the API's `requires_*` flags (sets / calls / pending-tasks) to the pill shape. */
export function requirementsFromFlags(o?: {
  requires_order?: boolean | null;
  requires_inventory?: boolean | null;
  requires_oos?: boolean | null;
  requires_photos?: boolean | null;
  requires_documents?: boolean | null;
}): TaskRequirements | undefined {
  if (!o) return undefined;
  return {
    order: !!o.requires_order,
    inventory: !!o.requires_inventory,
    oos: !!o.requires_oos,
    photos: !!o.requires_photos,
    documents: !!o.requires_documents,
  };
}

export default function RequirementPills({
  requirements,
  className = '',
}: {
  requirements?: TaskRequirements;
  className?: string;
}) {
  if (!hasAnyRequirement(requirements)) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`} aria-label="Task requirements">
      {PILL_DEFS.filter((d) => requirements![d.key]).map((d) => (
        <span
          key={d.key}
          title={`Requires: ${d.title}`}
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none ${d.cls}`}
        >
          {d.label}
        </span>
      ))}
    </div>
  );
}
